import {
    getYouTubeEmbedId,
    isAllowedEmbedSrc,
    isRumbleEmbedSrc,
    isSafeEmbedPoster,
} from './embeds';
import { resolveMediaUrl } from './media';
import { getVideoProviderLabel, getYouTubeThumbnailUrl } from './video';

// Click-to-play facades for YouTube/Rumble embeds inside rendered article
// bodies (site ArticlePage, legacy PostDetail). Each allowlisted iframe is
// swapped for a still + play button styled like the Condoned Media player
// (components/VideoEmbed.css — the page must have it loaded), so no
// third-party player loads until the reader asks for it. Pressing play puts
// the original iframe back where it was, with autoplay on. Stored article
// HTML is never changed, and the editor never runs this.

const FACADE_CLASS = 'ds-embed-facade';
// Set on an iframe the reader started, so a re-run leaves it playing.
const PLAYED_ATTRIBUTE = 'data-embed-played';

// The editor's data-poster gate (isSafeEmbedPoster): https, a server path
// (resolved against the API origin), or an API URL; anything else is ''.
const safePosterUrl = (value) => (isSafeEmbedPoster(value) ? resolveMediaUrl(value) : '');

// The stills to try, best first, or null when the embed gets no facade.
// A writer-set data-poster always wins. YouTube stills derive from the id
// (sharp maxresdefault, then the always-present hqdefault — the same order
// as VideoThumbnail); Rumble ids can't be turned into a still, so Rumble
// embeds without a captured data-poster keep the platform's player.
const getFacadeStills = (src, poster) => {
    const youTubeId = getYouTubeEmbedId(src);
    if (youTubeId) {
        return {
            provider: 'youtube',
            stills: [poster, getYouTubeThumbnailUrl(youTubeId, 'maxresdefault'), getYouTubeThumbnailUrl(youTubeId)]
                .filter(Boolean),
        };
    }
    if (isRumbleEmbedSrc(src) && poster) {
        return { provider: 'rumble', stills: [poster] };
    }
    return null;
};

// The player URL with autoplay on (YouTube 1, Rumble 2 — Rumble's "play
// now" value), keeping start times and every other parameter.
export const withAutoplay = (src, provider) => {
    try {
        const url = new URL(src);
        url.searchParams.set('autoplay', provider === 'rumble' ? '2' : '1');
        return url.toString();
    } catch {
        return src;
    }
};

// Shows the first still that loads; when all fail the image goes and the
// facade's dark frame and play badge remain.
const showFirstStill = (img, stills) => {
    let index = 0;
    const next = () => {
        index += 1;
        if (index < stills.length) {
            img.setAttribute('src', stills[index]);
        } else {
            img.remove();
        }
    };
    img.addEventListener('error', next);
    img.addEventListener('load', () => {
        // A missing maxres frame comes back as YouTube's 120×90 placeholder.
        if (stills[index].includes('/maxresdefault.') && img.naturalWidth <= 120) {
            next();
        }
    });
    img.setAttribute('src', stills[0]);
};

const buildFacade = (iframe, src, { provider, stills }) => {
    const doc = iframe.ownerDocument;
    const title = (iframe.getAttribute('title') || '').trim();
    const providerLabel = getVideoProviderLabel(provider);

    const wrapper = doc.createElement('div');
    wrapper.className = `ds-video-embed ${FACADE_CLASS}`;

    const button = doc.createElement('button');
    button.type = 'button';
    // grain-wrap: the site's film grain, and applyGrainWrap leaves it be.
    button.className = 'ds-video-embed-poster grain-wrap';
    button.setAttribute('aria-label', title ? `Play video: ${title}` : `Play ${providerLabel} video`);

    const img = doc.createElement('img');
    img.setAttribute('alt', '');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    showFirstStill(img, stills);

    const badge = doc.createElement('span');
    badge.className = 'ds-play-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = '▶';

    const tag = doc.createElement('span');
    tag.className = 'ds-video-embed-provider';
    tag.setAttribute('aria-hidden', 'true');
    tag.textContent = providerLabel;

    button.append(img, badge, tag);
    wrapper.appendChild(button);

    button.addEventListener('click', () => {
        iframe.setAttribute('src', withAutoplay(src, provider));
        // The reader asked for it: load now, and let the player autoplay
        // even when older markup didn't delegate the permission.
        iframe.removeAttribute('loading');
        const allow = iframe.getAttribute('allow') || '';
        if (!/(?:^|;)\s*autoplay\b/i.test(allow)) {
            iframe.setAttribute('allow', allow.trim() ? `${allow.trim().replace(/;$/, '')}; autoplay` : 'autoplay');
        }
        // Focus lands on the player, so it needs a name even when the
        // writer gave the embed no label.
        if (!title) {
            iframe.setAttribute('title', `${providerLabel} video`);
        }
        iframe.setAttribute(PLAYED_ATTRIBUTE, '');
        wrapper.replaceWith(iframe);
        // Focus follows the press into the player (keyboard/screen readers
        // land on the iframe that replaced the button).
        iframe.focus({ preventScroll: true });
    });

    return wrapper;
};

/**
 * Replaces each allowlisted YouTube/Rumble iframe in `container` with a
 * click-to-play facade, in place (figure, caption, alignment and width are
 * untouched). Idempotent: facades hold no iframe and started players are
 * marked, so re-running on the same DOM changes nothing, and fresh HTML
 * (React re-setting innerHTML) is simply processed again. Returns the
 * number of facades added.
 */
export const applyEmbedFacades = (container) => {
    if (!container || typeof container.querySelectorAll !== 'function') {
        return 0;
    }

    let added = 0;
    container.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.hasAttribute(PLAYED_ATTRIBUTE)
            || iframe.closest(`.${FACADE_CLASS}, [contenteditable="true"], .ProseMirror`)) {
            return;
        }
        const src = (iframe.getAttribute('src') || '').trim();
        if (!isAllowedEmbedSrc(src)) {
            return;
        }
        const facade = getFacadeStills(src, safePosterUrl(iframe.getAttribute('data-poster')));
        if (!facade) {
            return;
        }
        iframe.replaceWith(buildFacade(iframe, src, facade));
        added += 1;
    });
    return added;
};
